import { Alert, Button, CircularProgress, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { useGgaFreeSpinBetLevels } from '../../queries/casino';
import type { CashbackBonusFormValues } from '../../schema';
import FormNumberInput from '../molecules/FormNumberInput';

type Props = {
  disabled: boolean;
  gameId: string;
  index: number;
  isLevelGame: boolean;
};

const CashbackFreeSpinBetField = ({ disabled, gameId, index, isLevelGame }: Props) => {
  const {
    control,
    trigger,
    formState: { errors },
  } = useFormContext<CashbackBonusFormValues>();
  const betLevelsQuery = useGgaFreeSpinBetLevels(gameId, { enabled: isLevelGame });
  const betLevels = betLevelsQuery.data ?? [];
  const tierErrors = errors.freeSpinTiers?.[index];
  const betValueName = `freeSpinTiers.${index}.betValuePerSpin` as const;
  const betValue = useWatch({ control, name: betValueName });
  const hasBetValueError = !!tierErrors?.betValuePerSpin && tierErrors.betValuePerSpin.type !== 'server';

  useEffect(() => {
    if (hasBetValueError) {
      void trigger(betValueName);
    }
  }, [betValue, betValueName, hasBetValueError, trigger]);

  if (!isLevelGame) {
    return (
      <FormNumberInput
        control={control}
        name={`freeSpinTiers.${index}.betValuePerSpin`}
        label='Bet Value per Spin'
        error={tierErrors?.betValuePerSpin}
        allowDecimals
        decimalPlaces={2}
        valueAsString
        displayCurrency
        disabled={disabled}
        required
      />
    );
  }

  return (
    <>
      <Controller
        name={`freeSpinTiers.${index}.betValuePerSpin`}
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            select
            label='Bet Level'
            required
            value={field.value ?? ''}
            onChange={(event) => field.onChange(Number(event.target.value))}
            onBlur={field.onBlur}
            disabled={disabled || betLevelsQuery.isLoading || betLevelsQuery.isError || !betLevels.length}
            error={!!fieldState.error || !!tierErrors?.betValuePerSpin || betLevelsQuery.isError}
            helperText={
              fieldState.error?.message ??
              tierErrors?.betValuePerSpin?.message ??
              (betLevelsQuery.isError ? 'Unable to load bet levels for this game.' : undefined)
            }
          >
            {betLevels.map((level) => (
              <MenuItem key={level.value} value={level.value}>
                {level.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
      {betLevelsQuery.isLoading && (
        <Stack direction='row' spacing={1} sx={{ alignItems: 'center', gridColumn: '1 / -1' }}>
          <CircularProgress size={18} />
          <Typography variant='body2'>Loading bet levels…</Typography>
        </Stack>
      )}
      {betLevelsQuery.isError && (
        <Alert
          severity='error'
          action={<Button onClick={() => betLevelsQuery.refetch()}>Retry</Button>}
          sx={{ gridColumn: '1 / -1' }}
        >
          Unable to load bet levels for this game.
        </Alert>
      )}
      {betLevelsQuery.isSuccess && !betLevels.length && (
        <Alert severity='warning' sx={{ gridColumn: '1 / -1' }}>
          GGA returned no bet levels for this game.
        </Alert>
      )}
    </>
  );
};

export default CashbackFreeSpinBetField;
